import type { ExtendedFetchOptions } from '@/types/api-client'
import type {
	ConvertLeadsPayload,
	ConvertLeadsResponse,
	FetchLeadsPayload,
	FetchLeadsResponse
} from '@/types/api-client/leads'

/**
 * Leads API helper
 * Manages lead data fetching with optional filters
 */
export function createLeadsApi(
	request: <T>(endpoint: string, options?: ExtendedFetchOptions) => Promise<T | null>
) {
	return {
		/**
		 * Fetch leads with optional filters
		 * @param payload - Optional filter criteria (leadSource, isConverted)
		 * @param options - Optional fetch configuration
		 * @returns Promise<FetchLeadsResponse | null>
		 * @example
		 * ```typescript
		 * // Fetch all unconverted leads from address check
		 * await api.leads.fetch({ leadSource: 'address check', isConverted: false })
		 *
		 * // Fetch all leads (no filters)
		 * await api.leads.fetch({})
		 * ```
		 */
		fetch: (
			payload: FetchLeadsPayload = {},
			options: ExtendedFetchOptions = {}
		) => {
			return request<FetchLeadsResponse>(
				'/portal/leads',
				{
					method: 'POST',
					body: payload,
					...options
				}
			)
		},

		/**
		 * Convert a single lead to an account
		 * @param leadId - Lead ID to convert
		 * @param options - Optional fetch configuration
		 * @returns Promise<ConvertLeadsResponse | null>
		 * @example
		 * ```typescript
		 * // Convert lead ID 123 to an account
		 * await api.leads.convertSingle(123)
		 * ```
		 */
		convertSingle: (
			leadId: number,
			options: ExtendedFetchOptions = {}
		) => {
			return request<ConvertLeadsResponse>(
				`/portal/lead/${leadId}/convert`,
				{
					method: 'POST',
					...options
				}
			)
		},

		/**
		 * Convert multiple leads to accounts (bulk conversion)
		 * @param payload - Object containing array of lead IDs
		 * @param options - Optional fetch configuration
		 * @returns Promise<ConvertLeadsResponse | null>
		 * @example
		 * ```typescript
		 * // Convert leads 1, 2, 3 to accounts
		 * await api.leads.convertBulk({ leadIds: [1, 2, 3] })
		 * ```
		 */
		convertBulk: (
			payload: ConvertLeadsPayload,
			options: ExtendedFetchOptions = {}
		) => {
			return request<ConvertLeadsResponse>(
				'/portal/lead/bulk/convert',
				{
					method: 'POST',
					body: payload,
					...options
				}
			)
		}
	}
}
